import { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useCuisines } from "../hooks/useData";
import { ToastContext } from "../Components/ToastProvider";
import LoadingButton from "../Components/LoadingButton";

function RadioChip({ name, value, label, checked, onChange }) {
  return (
    <label className={`chip ${checked ? "chip--selected" : ""}`}>
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
      />
      {label}
    </label>
  );
}

function CheckboxChip({ id, value, label, checked, onChange }) {
  return (
    <label className={`chip ${checked ? "chip--selected" : ""}`}>
      <input
        type="checkbox"
        id={id}
        value={value}
        checked={checked}
        onChange={onChange}
        style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
      />
      {label}
    </label>
  );
}

function EditProfile() {
  const navigate = useNavigate();
  const { createToast } = useContext(ToastContext);
  const { profile, error, updateUser, changePassword } = useAuth();
  const { cuisines: cuisineChoices } = useCuisines();

  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [favoriteFood, setFavoriteFood] = useState("");
  const [askMeAbout, setAskMeAbout] = useState("");
  const [gender, setGender] = useState("");
  const [spiceLevel, setSpiceLevel] = useState("");
  const [age, setAge] = useState("");
  const [cuisines, setCuisines] = useState([]);
  const [newsletterSubscribed, setNewsletterSubscribed] = useState(true);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [disabled, setDisabled] = useState(false);

  const maxChars = 150;

  useEffect(() => {
    if (!profile) return;
    if (profile.name) setName(profile.name);
    if (profile.bio) setBio(profile.bio);
    if (profile.favorite_food) setFavoriteFood(profile.favorite_food);
    if (profile.ask_me_about) setAskMeAbout(profile.ask_me_about);
    if (profile.gender) setGender(profile.gender);
    if (profile.spice_level) setSpiceLevel(profile.spice_level);
    if (profile.age_range) setAge(profile.age_range);
    if (profile.cuisines?.length)
      setCuisines(profile.cuisines.map((c) => c.id));
    setNewsletterSubscribed(profile.newsletter_subscribed);
  }, [profile]);

  const handleCuisineToggle = (id) => {
    setCuisines((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  };

  const handleUpdateAccount = (event) => {
    event.preventDefault();
    setDisabled(true);
    updateUser({ name, newsletterSubscribed }).then((success) => {
      if (success) {
        createToast("Account updated successfully 🚀", "success", true);
        navigate("/dashboard/profile");
      } else {
        setDisabled(false);
      }
    });
  };

  const handleUpdateProfile = (event) => {
    event.preventDefault();
    if (!gender) {
      createToast("Please select your gender.", "error");
      return;
    }
    if (!age) {
      createToast("Please select your age range.", "error");
      return;
    }
    if (!spiceLevel) {
      createToast("Please select your spice level.", "error");
      return;
    }
    if (cuisines.length === 0) {
      createToast("Please select at least one cuisine.", "error");
      return;
    }
    setDisabled(true);
    updateUser({
      bio,
      favoriteFood,
      gender,
      spiceLevel,
      askMeAbout,
      age,
      cuisineIds: cuisines,
    }).then((success) => {
      if (success) {
        createToast("Profile updated successfully 🚀", "success", true);
        navigate("/dashboard/profile");
      } else {
        setDisabled(false);
      }
    });
  };

  const handleChangePassword = (event) => {
    event.preventDefault();
    if (password !== passwordConfirm) {
      createToast("The two passwords don't match.", "error");
      return;
    }
    setDisabled(true);
    changePassword(password, passwordConfirm, profile.id).then((success) => {
      if (success) {
        createToast("Password changed successfully", "success", true);
        navigate("/dashboard/profile");
      } else {
        setDisabled(false);
      }
    });
  };

  if (!profile) return <p>Loading...</p>;
  if (error) return <p className="error-message">Something went wrong...</p>;

  const genderOptions = [
    { value: "Woman", label: "Woman" },
    { value: "Man", label: "Man" },
    { value: "Non-Binary", label: "Non-binary" },
    { value: "Rather not say", label: "Rather not say" },
  ];

  const ageOptions = ["20s", "30s", "40s", "50s", "60s", "70s", "80+"];

  const spiceOptions = [
    { value: "🌿 Low", label: "🌿 Low" },
    { value: "🌶️ Medium", label: "🌶️ Medium" },
    { value: "🔥 High", label: "🔥 High" },
  ];

  return (
    <div className="edit-profile-page">
      <div className="edit-profile-header">
        <h1>Edit Profile</h1>
        <p>
          This is what fellow foodies will see before sitting down with you.
          Make it feel like <em>you</em>.
        </p>
      </div>

      {/* ── CARD 1: ACCOUNT ── */}
      <form onSubmit={handleUpdateAccount}>
        <section className="profile-card">
          <p className="card-section-label">Account</p>

          <div className="field">
            <label className="field-label" htmlFor="username">
              Username
            </label>
            <p className="field-hint">
              How fellow foodies will know you on Fudopia.
            </p>
            <input
              id="username"
              type="text"
              value={profile.username}
              disabled
            />
          </div>

          <div className="field">
            <label className="field-label" htmlFor="name">
              Your Name <span className="required-field">*</span>
            </label>
            <p className="field-hint">
              Only visible to you and us — never shown on your profile.
            </p>
            <input
              id="name"
              type="text"
              value={name}
              required
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="field-label" htmlFor="email">
              Email
            </label>
            <p className="field-hint">
              Used for event confirmations. Not shown publicly.{" "}
              <span className="field-note">
                Need to change it? Use the contact page to reach out.
              </span>
            </p>
            <input id="email" type="email" value={profile.email} disabled />
          </div>

          <div className="field-divider" />

          <div className="field">
            <label className="newsletter-row">
              <input
                type="checkbox"
                checked={newsletterSubscribed}
                onChange={(e) => setNewsletterSubscribed(e.target.checked)}
              />
              <div className="newsletter-copy">
                <strong>Fudopia Weekly 📬</strong>
                <span>
                  A short weekly email: new events, food finds, and the
                  occasional thing we think you'd love. No spam. Unsubscribe
                  anytime.
                </span>
              </div>
            </label>
          </div>

          <div className="save-row save-row--secondary">
            <LoadingButton
              type="submit"
              color="primary"
              variant="contained"
              disabled={disabled}
            >
              Save Account
            </LoadingButton>
          </div>
        </section>
      </form>

      {/* ── CARD 2: PASSWORD ── */}
      <form onSubmit={handleChangePassword}>
        <section className="profile-card">
          <p className="card-section-label">Password</p>

          <div className="field">
            <label className="field-label" htmlFor="password">
              Change Password
            </label>
            <p className="field-hint">
              At least 8 characters, one uppercase letter, and one number.
            </p>
            <input
              id="password"
              type="password"
              value={password}
              placeholder="New password"
              minLength="8"
              pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}"
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="field-label" htmlFor="password2">
              Confirm New Password
            </label>
            <input
              id="password2"
              type="password"
              value={passwordConfirm}
              placeholder="Type it again"
              minLength="8"
              pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}"
              onChange={(e) => setPasswordConfirm(e.target.value)}
            />
          </div>

          <div className="save-row">
            <LoadingButton
              type="submit"
              color="primary"
              variant="contained"
              disabled={disabled}
            >
              Update Password
            </LoadingButton>
          </div>
        </section>
      </form>

      {/* ── CARD 3: FOODIE PROFILE ── */}
      <form onSubmit={handleUpdateProfile}>
        <section className="profile-card">
          <p className="card-section-label">Your Foodie Profile</p>

          <div className="field">
            <label className="field-label">
              Gender <span className="required-field">*</span>
            </label>
            <p className="field-hint">
              Shown on your profile so people have a sense of who they'll be
              meeting.
            </p>
            <div className="chips">
              {genderOptions.map((opt) => (
                <RadioChip
                  key={opt.value}
                  name="gender"
                  value={opt.value}
                  label={opt.label}
                  checked={gender === opt.value}
                  onChange={(e) => setGender(e.target.value)}
                />
              ))}
            </div>
          </div>

          <div className="field">
            <label className="field-label">
              Age Range <span className="required-field">*</span>
            </label>
            <p className="field-hint">
              Shown on your profile, helps people get a sense of who they'll be
              meeting.
            </p>
            <div className="chips">
              {ageOptions.map((opt) => (
                <RadioChip
                  key={opt}
                  name="age"
                  value={opt}
                  label={opt}
                  checked={age === opt}
                  onChange={(e) => setAge(e.target.value)}
                />
              ))}
            </div>
          </div>

          <div className="field-divider" />

          <div className="field">
            <label className="field-label" htmlFor="favoriteFood">
              Favourite Dish of All Time{" "}
              <span className="required-field">*</span>
            </label>
            <p className="field-hint">
              The one dish you'd eat forever without getting bored. Shown on
              your profile. Guaranteed conversation starter.
            </p>
            <input
              id="favoriteFood"
              type="text"
              value={favoriteFood}
              required
              placeholder="e.g. Pad Kra Pao, my mum's lasagna, a perfect bowl of ramen…"
              onChange={(e) => setFavoriteFood(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="field-label">
              How Spicy Do You Like It?{" "}
              <span className="required-field">*</span>
            </label>
            <p className="field-hint">
              We use this to suggest events and menus that match your heat
              level.
            </p>
            <div className="chips">
              {spiceOptions.map((opt) => (
                <RadioChip
                  key={opt.value}
                  name="spiceLevel"
                  value={opt.value}
                  label={opt.label}
                  checked={spiceLevel === opt.value}
                  onChange={(e) => setSpiceLevel(e.target.value)}
                />
              ))}
            </div>
          </div>

          <div className="field">
            <label className="field-label">
              Cuisines I'm Into <span className="required-field">*</span>
            </label>
            <p className="callout">
              Helps us suggest the right events for you, and gives fellow
              Fudopians a taste of what you're into.
            </p>
            <div className="chips">
              {cuisineChoices?.map((cuisine) => (
                <CheckboxChip
                  key={cuisine.id}
                  id={`cuisine-${cuisine.id}`}
                  value={cuisine.id}
                  label={cuisine.title}
                  checked={cuisines.includes(cuisine.id)}
                  onChange={() => handleCuisineToggle(cuisine.id)}
                />
              ))}
            </div>
          </div>

          <div className="field-divider" />

          <div className="field">
            <label className="field-label" htmlFor="askMeAbout">
              Ask Me About… <span className="required-field">*</span>
            </label>
            <p className="field-hint">
              Your thing right now, food-related or not. A cuisine obsession, a
              hobby, a city you love, something you're learning.
            </p>
            <input
              id="askMeAbout"
              type="text"
              value={askMeAbout}
              maxLength={maxChars}
              required
              placeholder="e.g. natural wine, ultrarunning, Northern Thai street food, ceramics…"
              onChange={(e) => setAskMeAbout(e.target.value)}
            />
            <p className="remark">
              {askMeAbout.length} / {maxChars}
            </p>
          </div>

          <div className="field">
            <label className="field-label" htmlFor="bio">
              Bio
            </label>
            <p className="field-hint">
              A food memory, a strong opinion, your go-to order. Anything goes.
            </p>
            <textarea
              id="bio"
              value={bio}
              rows="6"
              placeholder="e.g. I'll try anything once, forever chasing the ramen I had in Fukuoka in 2019…"
              onChange={(e) => setBio(e.target.value)}
            />
          </div>
        </section>

        <div className="save-row">
          <LoadingButton
            type="submit"
            color="primary"
            variant="contained"
            disabled={disabled}
          >
            Save Profile
          </LoadingButton>
        </div>
      </form>

      <Link to="/dashboard/profile" className="back-button">
        ← Back to My Profile
      </Link>
    </div>
  );
}

export default EditProfile;
